import { useRef, type PointerEvent as ReactPointerEvent, type MouseEvent as ReactMouseEvent } from 'react';

/** How far the pointer has to travel before this is a drag and not a click that wobbled. */
const SLOP = 4;

/**
 * Drag a scroll-snap track with the pointer. MOUSE AND PEN ONLY — a touch already scrolls these
 * natively, with momentum and rubber-banding we would only approximate, and taking that gesture would
 * take the page's vertical one with it.
 *
 * Three things it has to do that are not obvious:
 *
 * Turn off `scroll-behavior` as well as snapping. `.carousel` sets it to `smooth`, so assigning
 * `scrollLeft` does not move the track, it ANIMATES it — and a drag assigns a new target every
 * pointermove, so each one restarts an ease that never arrives. Measured, a 700px gesture moved the
 * track 47px.
 *
 * Hand the landing to the caller, BEFORE snapping comes back. Restoring `scroll-snap-type` does not
 * re-snap on its own, and restoring it first lets the browser move the track somewhere of its own
 * choosing — which the caller then measures instead of where the drag ended.
 *
 * Swallow the click a real drag ends on, since the cards are clickable, and only past `SLOP` so an
 * ordinary click is never eaten.
 */
export function useDragScroll<T extends HTMLElement>(options: { onDragEnd?: () => void } = {}) {
	const drag = useRef<{ id: number; x: number; left: number; moved: boolean } | null>(null);
	const swallowClick = useRef(false);

	const onPointerDown = (event: ReactPointerEvent<T>) => {
		// Left button only: a right-click opens a menu and a middle-click is the browser's own scroll.
		if (event.pointerType === 'touch' || event.button !== 0) return;
		const el = event.currentTarget;
		drag.current = { id: event.pointerId, x: event.clientX, left: el.scrollLeft, moved: false };
		swallowClick.current = false;
		el.setPointerCapture(event.pointerId);
		el.style.cursor = 'grabbing';
	};

	const onPointerMove = (event: ReactPointerEvent<T>) => {
		const state = drag.current;
		if (!state || state.id !== event.pointerId) return;
		const dx = event.clientX - state.x;
		if (!state.moved) {
			if (Math.abs(dx) < SLOP) return;
			state.moved = true;
			// Only now, so a plain click never disturbs the track's own settings at all.
			event.currentTarget.style.scrollSnapType = 'none';
			event.currentTarget.style.scrollBehavior = 'auto';
		}
		event.currentTarget.scrollLeft = state.left - dx;
	};

	const onPointerUp = (event: ReactPointerEvent<T>) => {
		const state = drag.current;
		if (!state || state.id !== event.pointerId) return;
		const el = event.currentTarget;
		drag.current = null;
		swallowClick.current = state.moved;
		el.style.cursor = '';
		if (el.hasPointerCapture(event.pointerId)) el.releasePointerCapture(event.pointerId);
		// Smooth scrolling first, so the caller's landing glides the way the rail's does.
		el.style.scrollBehavior = '';
		// Before snapping returns — see above. Only after a real drag: a click leaves the track alone.
		if (state.moved) options.onDragEnd?.();
		el.style.scrollSnapType = '';
	};

	const onClickCapture = (event: ReactMouseEvent<T>) => {
		if (!swallowClick.current) return;
		swallowClick.current = false;
		event.preventDefault();
		event.stopPropagation();
	};

	return {
		style: { cursor: 'grab' },
		onPointerDown,
		onPointerMove,
		onPointerUp,
		onPointerCancel: onPointerUp,
		onClickCapture,
		// Dragging an image starts the browser's own drag-and-drop, which cuts the pointer stream.
		onDragStart: (event: ReactMouseEvent<T>) => event.preventDefault(),
	};
}
